import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Pagination,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Icon,
  DatePicker,
  ActionsMenu,
  ActionsMenuItem,
  ActionsMenuDivider,
  showToast,
  Input,
  Select,
} from '@repo/ui';
import { useTrustRequestTransactions, useBatchNames } from '../queries';
import { useClientsForFiltering } from '../queries';
import { useFilteredUsersQuery } from '@xtnd/web/features/juniper/queries';
import { formatCurrency, formatDate, DateFormat } from '@xtnd/web/shared/utils/format';
import type { TrustRequestTransaction } from '../types/trustRequestTransactionTypes';

export function TrustRequestsTransactionsView() {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Date range state
  const getDefaultDateRange = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    return {
      startDate: thirtyDaysAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
    };
  };

  const [startDate, setStartDate] = useState(() => {
    const urlStartDate = searchParams.get('startDate');
    return urlStartDate || getDefaultDateRange().startDate;
  });

  const [endDate, setEndDate] = useState(() => {
    const urlEndDate = searchParams.get('endDate');
    return urlEndDate || getDefaultDateRange().endDate;
  });

  // Filter state
  const [selectedBatchNames, setSelectedBatchNames] = useState<string[]>([]);
  const [matterSearchQuery, setMatterSearchQuery] = useState('');
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedCreatedBy, setSelectedCreatedBy] = useState<string>('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  
  // Selection state
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(new Set());
  
  // Action menu state
  const [actionsMenuOpen, setActionsMenuOpen] = useState<string | null>(null);

  // Fetch data
  const { data: transactionsData, isLoading: isLoadingTransactions } = useTrustRequestTransactions({
    dateFrom: startDate,
    dateTo: endDate,
    batchName: selectedBatchNames.length > 0 ? selectedBatchNames[0] : undefined,
    matterId: matterSearchQuery || undefined,
    clientIds: selectedClientIds.length > 0 ? selectedClientIds : undefined,
    status: selectedStatuses.length > 0 ? selectedStatuses : undefined,
    createdBy: selectedCreatedBy || undefined,
    page: currentPage,
    limit: pageSize,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const { data: batchNamesData } = useBatchNames();
  const { data: clientsData } = useClientsForFiltering({ hasJuniperEnabled: true });
  const { data: userNames } = useFilteredUsersQuery(transactionsData?.data || []);

  // Transform data for Select components
  const batchOptions = useMemo(() => {
    if (!batchNamesData?.data) return [];
    return batchNamesData.data.map(batch => ({
      value: batch.batchCode,
      label: `${batch.name} (${batch.requestCount} requests)`,
    }));
  }, [batchNamesData]);

  const clientOptions = useMemo(() => {
    if (!clientsData?.data) return [];
    return clientsData.data.map(client => ({
      value: client.id,
      label: client.name,
    }));
  }, [clientsData]);

  const userOptions = useMemo(() => {
    if (!userNames) return [];
    return Object.entries(userNames).map(([userId, userName]) => ({
      value: userId,
      label: userName,
    }));
  }, [userNames]);

  const transactions = transactionsData?.data || [];
  const summary = transactionsData?.summary || {
    totalRequests: 0,
    byStatus: {},
    totalAmount: 0,
    byBatch: {},
  };

  // Status badge colors
  const getStatusColor = (status: string): string => {
    const baseClass = 'inline-flex px-2 py-1 text-xs font-semibold rounded-full';
    switch (status.toLowerCase()) {
      case 'completed':
        return `${baseClass} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'processing':
        return `${baseClass} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`;
      case 'pending':
        return `${baseClass} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'failed':
      case 'cancelled':
        return `${baseClass} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
      default:
        return `${baseClass} bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200`;
    }
  };

  const handleSelectAll = () => {
    if (selectedTransactions.size === transactions.length && transactions.length > 0) {
      setSelectedTransactions(new Set());
    } else {
      setSelectedTransactions(new Set(transactions.map(t => t.juniperTRRId)));
    }
  };

  const handleToggleSelection = (juniperTRRId: string) => {
    const newSelection = new Set(selectedTransactions);
    if (newSelection.has(juniperTRRId)) {
      newSelection.delete(juniperTRRId);
    } else {
      newSelection.add(juniperTRRId);
    }
    setSelectedTransactions(newSelection);
  };

  return (
    <div className="space-y-6">
      {/* Filters Section */}
      <Card variant="outlined" padding="lg">
        <CardHeader>
          <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">Filters</h3>
        </CardHeader>
        <CardBody>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {/* Date Range - Start Date */}
            <div className="space-y-2">
              <DatePicker
                mode="single"
                label="Start Date"
                value={startDate ? new Date(startDate) : null}
                onChange={(date) => {
                  if (date) {
                    setStartDate(date.toISOString().split('T')[0]);
                  }
                }}
              />
            </div>

            {/* Date Range - End Date */}
            <div className="space-y-2">
              <DatePicker
                mode="single"
                label="End Date"
                value={endDate ? new Date(endDate) : null}
                onChange={(date) => {
                  if (date) {
                    setEndDate(date.toISOString().split('T')[0]);
                  }
                }}
              />
            </div>

            {/* Batch Name Filter */}
            <div className="space-y-2">
              <Select
                label="Batch Name"
                options={batchOptions}
                value={selectedBatchNames.length > 0 ? selectedBatchNames.map(name => ({ value: name, label: batchOptions.find(opt => opt.value === name)?.label || name })) : null}
                onChange={(value) => {
                  if (Array.isArray(value)) {
                    setSelectedBatchNames(value.map(item => typeof item === 'string' ? item : item.value));
                  } else {
                    setSelectedBatchNames([]);
                  }
                }}
                placeholder="Select batch..."
                multiple
                searchable
                clearable
                fullWidth
              />
            </div>

            {/* Matter Search */}
            <div className="space-y-2">
              <Input
                label="Matter Display Number"
                value={matterSearchQuery}
                onChange={(e) => setMatterSearchQuery(e.target.value)}
                placeholder="Search by matter number..."
                fullWidth
              />
            </div>

            {/* Client Filter */}
            <div className="space-y-2">
              <Select
                label="Client"
                options={clientOptions}
                value={selectedClientIds.length > 0 ? selectedClientIds.map(id => ({ value: id, label: clientOptions.find(opt => opt.value === id)?.label || id })) : null}
                onChange={(value) => {
                  if (Array.isArray(value)) {
                    setSelectedClientIds(value.map(item => typeof item === 'string' ? item : item.value));
                  } else {
                    setSelectedClientIds([]);
                  }
                }}
                placeholder="Select clients..."
                multiple
                searchable
                clearable
                fullWidth
              />
            </div>

            {/* Status Filter */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-xtnd-dark dark:text-xtnd-light mb-2">
                Status
              </label>
              <div className="space-y-2">
                {['pending', 'processing', 'completed', 'failed', 'cancelled'].map(status => (
                  <label key={status} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedStatuses.includes(status)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedStatuses([...selectedStatuses, status]);
                        } else {
                          setSelectedStatuses(selectedStatuses.filter(s => s !== status));
                        }
                      }}
                      className="w-4 h-4 text-xtnd-blue bg-gray-100 border-gray-300 rounded focus:ring-xtnd-blue"
                    />
                    <span className="text-sm text-xtnd-dark dark:text-xtnd-light capitalize">{status}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Created By Filter */}
            <div className="space-y-2">
              <Select
                label="Created By"
                options={userOptions}
                value={selectedCreatedBy ? userOptions.find(opt => opt.value === selectedCreatedBy) || null : null}
                onChange={(value) => {
                  if (value && typeof value === 'object' && 'value' in value) {
                    setSelectedCreatedBy(value.value);
                  } else {
                    setSelectedCreatedBy('');
                  }
                }}
                placeholder="Select user..."
                searchable
                clearable
                fullWidth
              />
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card variant="outlined" padding="md">
          <CardBody>
            <div className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Total Requests</div>
            <div className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-light">{summary.totalRequests}</div>
          </CardBody>
        </Card>
        <Card variant="outlined" padding="md">
          <CardBody>
            <div className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Total Amount</div>
            <div className="text-2xl font-bold text-xtnd-dark dark:text-xtnd-light">{formatCurrency(summary.totalAmount)}</div>
          </CardBody>
        </Card>
        <Card variant="outlined" padding="md">
          <CardBody>
            <div className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Completed</div>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">{summary.byStatus.completed || 0}</div>
          </CardBody>
        </Card>
        <Card variant="outlined" padding="md">
          <CardBody>
            <div className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">Pending</div>
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{summary.byStatus.pending || 0}</div>
          </CardBody>
        </Card>
      </div>

      {/* Transactions Table */}
      <Card variant="outlined" padding="lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
              Trust Request Transactions
            </h3>
            {selectedTransactions.size > 0 && (
              <span className="text-sm text-xtnd-blue">
                {selectedTransactions.size} selected
              </span>
            )}
          </div>
        </CardHeader>
        <CardBody>
          {isLoadingTransactions ? (
            <div className="text-center py-8">
              <Icon name="HiArrowPath" size="xl" className="mx-auto mb-4 animate-spin text-xtnd-blue" />
              <p className="text-xtnd-dark-600 dark:text-xtnd-light-400">Loading transactions...</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="HiInformationCircle" size="xl" className="mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-semibold mb-2 text-xtnd-dark dark:text-xtnd-light">No trust requests found</h3>
              <p className="text-xtnd-dark-600 dark:text-xtnd-light-400">Try adjusting your filters to see more results.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-xtnd-light dark:bg-xtnd-dark-800">
                  <tr>
                    <th className="w-12 px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedTransactions.size === transactions.length && transactions.length > 0}
                        onChange={handleSelectAll}
                        className="w-4 h-4 text-xtnd-blue bg-gray-100 border-gray-300 rounded focus:ring-xtnd-blue"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">TRR ID</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">Batch Name</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">Matter</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">Client</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">Total</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">Paid</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">Pending</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">Last Sent</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">Created</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">Created By</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction) => (
                    <tr
                      key={transaction.juniperTRRId}
                      className={`border-b border-xtnd-light dark:border-xtnd-dark-600 hover:bg-xtnd-light dark:hover:bg-xtnd-dark-800 ${
                        selectedTransactions.has(transaction.juniperTRRId) ? 'bg-xtnd-blue/10' : ''
                      }`}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedTransactions.has(transaction.juniperTRRId)}
                          onChange={() => handleToggleSelection(transaction.juniperTRRId)}
                          className="w-4 h-4 text-xtnd-blue bg-gray-100 border-gray-300 rounded focus:ring-xtnd-blue"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-xtnd-blue dark:text-xtnd-blue-300 font-mono">
                        <div className="flex flex-col">
                          <span>{transaction.trustRequestId || transaction.juniperTRRId}</span>
                          {transaction.trustRequestBill?.state && (
                            <span className="text-xs text-xtnd-dark-500 dark:text-xtnd-light-500">
                              {transaction.trustRequestBill.state}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-xtnd-dark dark:text-xtnd-light">
                        {transaction.batchName || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-xtnd-dark dark:text-xtnd-light">
                        {transaction.matterDisplayNumber || transaction.matterId}
                      </td>
                      <td className="px-4 py-3 text-sm text-xtnd-dark dark:text-xtnd-light">
                        {transaction.clientName || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-mono text-xtnd-dark dark:text-xtnd-light">
                        {transaction.trustRequestBill?.total ? formatCurrency(transaction.trustRequestBill.total) : formatCurrency(transaction.amount)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-mono text-xtnd-dark dark:text-xtnd-light">
                        {transaction.trustRequestBill?.paid ? formatCurrency(transaction.trustRequestBill.paid) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-mono text-xtnd-dark dark:text-xtnd-light">
                        {transaction.trustRequestBill?.pending ? formatCurrency(transaction.trustRequestBill.pending) : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={getStatusColor(transaction.status)}>
                          {transaction.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">
                        {transaction.lastSentAt ? (
                          <div className="flex flex-col">
                            <span>{formatDate(new Date(transaction.lastSentAt), DateFormat.US)}</span>
                            <span className="text-xs text-xtnd-dark-500 dark:text-xtnd-light-500">
                              {new Date(transaction.lastSentAt).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </span>
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">
                        {formatDate(new Date(transaction.createdAt), DateFormat.US)}
                      </td>
                      <td className="px-4 py-3 text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">
                        {transaction.createdByUserName || userNames?.[transaction.createdByUserId] || '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={(e) => {
                            setActionsMenuOpen(actionsMenuOpen === transaction.juniperTRRId ? null : transaction.juniperTRRId);
                          }}
                          className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                          title="More actions"
                        >
                          <Icon
                            name="HiDotsVertical"
                            className="h-5 w-5 text-gray-500 dark:text-gray-400"
                          />
                        </button>
                        <ActionsMenu
                          isOpen={actionsMenuOpen === transaction.juniperTRRId}
                          onClose={() => setActionsMenuOpen(null)}
                          triggerElement={null}
                        >
                          {transaction.status === 'completed' && transaction.documents?.pdf && (
                            <ActionsMenuItem
                              icon="HiDownload"
                              iconColor="text-green-600"
                              label="View/Download PDF"
                              onClick={() => {
                                // TODO: Implement PDF viewing
                                showToast.info('PDF viewing coming soon');
                                setActionsMenuOpen(null);
                              }}
                            />
                          )}
                          {transaction.status === 'failed' && (
                            <ActionsMenuItem
                              icon="HiRefresh"
                              iconColor="text-yellow-600"
                              label="Regenerate"
                              onClick={() => {
                                // TODO: Implement regeneration
                                showToast.info('Regeneration coming soon');
                                setActionsMenuOpen(null);
                              }}
                            />
                          )}
                          <ActionsMenuItem
                            icon="HiRefresh"
                            iconColor="text-blue-600"
                            label="Refresh Status"
                            onClick={() => {
                              // TODO: Implement refresh
                              showToast.info('Refresh coming soon');
                              setActionsMenuOpen(null);
                            }}
                          />
                        </ActionsMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {transactionsData?.pagination && transactionsData.pagination.totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">
                Page {currentPage} of {transactionsData.pagination.totalPages} ({transactionsData.pagination.total} total)
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  size="sm"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.min(transactionsData.pagination.totalPages, prev + 1))}
                  disabled={currentPage === transactionsData.pagination.totalPages}
                  size="sm"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

